import { ROUTING_PATHS } from 'const';
import { useIntParam, useSafeMutation } from 'hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';
import { speakingSessionStore } from 'store';
import { parseServerErrors } from 'utils';

export const useAddRoomToSpeakingSession = () => {
  const { speakingSessionDetails } = ROUTING_PATHS;
  const navigate = useNavigate();
  const [selectedRooms, setSelectedRooms] = useState<number[]>([]);
  const speakingSessionId = useIntParam('sessionId');

  const [
    addRooms,
    { isSuccess: addedRooms, isLoading: isAddingRooms, error: savingError },
  ] = speakingSessionStore.service.speakingSessionApi.useAssignRoomsToSpeakingSessionMutation();
  const errors = useMemo(() => parseServerErrors(savingError), [savingError]);

  const { data: availableRooms, isLoading } =
    speakingSessionStore.service.speakingSessionApi.useGetAvailableRoomsQuery(
      {
        speakingSessionId,
      },
      {
        skip: addedRooms,
      });

  useEffect(() => {
    if (availableRooms) {
      const availableRoomIds = new Set(availableRooms.map(room => room.id));
      setSelectedRooms(prev => prev.filter(roomId => availableRoomIds.has(roomId)));
    } else {
      setSelectedRooms([]);
    }
  }, [availableRooms]);

  const goToSpeakingSessionDetails = useCallback(() => {
    navigate(generatePath(speakingSessionDetails.absolutePath, { sessionId: speakingSessionId }));
  }, [navigate, speakingSessionDetails.absolutePath, speakingSessionId]);

  const { execute: addRoomsToSession } = useSafeMutation(addRooms, {
    successToastMessage: 'Changes saved successfully',
    onSuccess: goToSpeakingSessionDetails,
  });

  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [delayedSearchTerm, setDelayedSearchTerm] = useState<string | undefined>(undefined);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDelayedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  return {
    speakingSessionId,
    availableRooms,
    isLoading,
    addRoomsToSession,
    isAddingRooms,
    addedRooms,
    errors,
    selectedRooms,
    setSelectedRooms,
    searchTerm: delayedSearchTerm,
    setSearchTerm,
  };
};
